import { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import KPICard from '../components/common/KPICard'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import LineChart from '../components/charts/LineChart'
import BarChart from '../components/charts/BarChart'
import {
  CurrencyDollarIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  UserIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import {
  formatCurrency,
  formatMonth,
  formatDate,
  getCountryName
} from '../utils/formatters'

export default function RevenueCustomerReport() {
  const { customerId: urlCustomerId } = useParams()
  const navigate = useNavigate()
  const { data, computed, loading, error } = useData()
  const [selectedCustomerId, setSelectedCustomerId] = useState(urlCustomerId || '')

  const customerId = urlCustomerId || selectedCustomerId

  const customerData = useMemo(() => {
    if (!data || !computed || !customerId) return null

    const customer = data.customers.find(c => c.customer_id === customerId)
    if (!customer) return null

    // Customer ARR history
    const arrHistory = data.monthlyArr
      .filter(r => r.customer_id === customerId)
      .sort((a, b) => a.month.localeCompare(b.month))

    const months = arrHistory.map(r => r.month)
    const arrValues = arrHistory.map(r => r.arr_value)

    // Customer movements
    const movements = data.arrMovements
      .filter(m => m.customer_id === customerId)
      .sort((a, b) => b.month.localeCompare(a.month))

    return {
      customer,
      months,
      arrValues,
      movements
    }
  }, [data, computed, customerId])

  const handleCustomerSelect = (id) => {
    setSelectedCustomerId(id)
    if (id) {
      navigate(`/revenue/customer/${id}`)
    }
  }

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>

  return (
    <div className="space-y-6">
      {/* Customer Selector */}
      <div className="card p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Customer
        </label>
        <select
          value={customerId}
          onChange={(e) => handleCustomerSelect(e.target.value)}
          className="select max-w-md"
        >
          <option value="">Choose a customer...</option>
          {data?.customers
            .sort((a, b) => a.organization_name.localeCompare(b.organization_name))
            .map(c => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.organization_name} ({c.customer_id})
              </option>
            ))}
        </select>
      </div>

      {customerData ? (
        <>
          {/* Customer Info */}
          <div className="card p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center">
                    <BuildingOfficeIcon className="w-6 h-6 text-brand-600 dark:text-brand-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {customerData.customer.organization_name}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {customerData.customer.customer_id} &middot; {customerData.customer.alias}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Country</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {getCountryName(customerData.customer.country)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subscription</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customerData.customer.subscription_type}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Revenue Stream</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customerData.customer.revenue_stream}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Account Manager</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customerData.customer.account_manager}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <span className={`badge ${
                  customerData.customer.status === 'active' ? 'badge-success' : 'badge-danger'
                }`}>
                  {customerData.customer.status}
                </span>
                {customerData.customer.app_link && (
                  <a
                    href={customerData.customer.app_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-sm"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                    Open App
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Current ARR"
              value={formatCurrency(customerData.customer.current_arr)}
              icon={CurrencyDollarIcon}
            />
            <KPICard
              title="Revenue Collected"
              value={formatCurrency(customerData.customer.total_revenue_collected)}
              icon={CurrencyDollarIcon}
              colorScheme="success"
            />
            <KPICard
              title="Lifetime (Months)"
              value={customerData.customer.lifetime_months}
              icon={CalendarDaysIcon}
            />
            <KPICard
              title="Customer Since"
              value={formatDate(customerData.customer.customer_since)}
              icon={UserIcon}
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Annual Recurring Revenue" subtitle="Customer ARR history" height="h-64">
              <LineChart
                labels={customerData.months}
                datasets={[{
                  label: 'ARR',
                  data: customerData.arrValues,
                  color: '#3b82f6'
                }]}
                fill={true}
              />
            </ChartCard>

            <ChartCard title="Recent Movement" height="h-auto">
              <DataTable
                columns={[
                  { key: 'month', header: 'Month', render: v => formatMonth(v) },
                  { key: 'organization_name', header: 'Organization' },
                  { key: 'country', header: 'Country', render: v => getCountryName(v) },
                  { key: 'arr_change', header: 'ARR Change', render: v => (
                    <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                      {formatCurrency(v)}
                    </span>
                  )},
                  { key: 'movement_type', header: 'Movement', render: v => (
                    <span className={`badge ${
                      v === 'New Business' || v.includes('Expansion') ? 'badge-success' :
                      v === 'Churn' ? 'badge-danger' : 'badge-warning'
                    }`}>
                      {v}
                    </span>
                  )}
                ]}
                data={customerData.movements}
                pageSize={5}
              />
            </ChartCard>
          </div>
        </>
      ) : (
        <div className="card p-12 text-center">
          <UserIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Customer Selected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Select a customer from the dropdown above to view their details.
          </p>
        </div>
      )}
    </div>
  )
}
